import { builder } from '@builder.io/sdk'
import { PageOptions } from '@graphcommerce/framer-next-pages'
import { hygraphPageContent, HygraphPagesQuery } from '@graphcommerce/graphcms-ui'
import { StoreConfigDocument } from '@graphcommerce/magento-store'
import {
  PageMeta,
  GetStaticProps,
  Pagination,
  LayoutTitle,
  LayoutHeader,
} from '@graphcommerce/next-ui'
import { Container, Link } from '@mui/material'
import { GetStaticPaths } from 'next'
import { useRouter } from 'next/router'
import React from 'react'
import {
  BlogList,
  BlogListDocument,
  BlogListQuery,
  BlogPathsDocument,
  BlogPathsQuery,
  LayoutDocument,
  LayoutNavigation,
  LayoutNavigationProps,
  RowRenderer,
} from '../../../components'
import { graphqlSsrClient, graphqlSharedClient } from '../../../lib/graphql/graphqlSsrClient'

builder.init(process.env.NEXT_PUBLIC_BUILDER_API_KEY!)

type Props = HygraphPagesQuery & BlogListQuery & { count?: number }
type RouteProps = { page: string }
type GetPageStaticPaths = GetStaticPaths<RouteProps>
type GetPageStaticProps = GetStaticProps<LayoutNavigationProps, Props, RouteProps>

const pageSize = 16

function BlogPage(props: Props) {
  const { pages, blogPosts, count = 0 } = props
  const router = useRouter()
  const page = pages[0]
  const title = page.title ?? ''

  return (
    <>
      <PageMeta title={title} metaDescription={title} canonical={`/${page.url}`} />

      <LayoutHeader floatingMd>
        <LayoutTitle size='small' component='span'>
          {title}
        </LayoutTitle>
      </LayoutHeader>

      <Container maxWidth='xl'>
        <LayoutTitle variant='h1'>{title}</LayoutTitle>
      </Container>

      <BlogList blogPosts={blogPosts} />
      <Pagination
        count={Math.ceil(count / pageSize)}
        page={Number(router.query.page ? router.query.page : 1)}
        renderLink={(p: number, icon: React.ReactNode) => (
          <Link href={p === 1 ? '/blog' : `/blog/page/${p}`} color='primary' underline='hover'>
            {icon}
          </Link>
        )}
      />

      <RowRenderer content={page.content} />
    </>
  )
}

BlogPage.pageOptions = {
  Layout: LayoutNavigation,
} as PageOptions

export default BlogPage

// eslint-disable-next-line @typescript-eslint/require-await
export const getStaticPaths: GetPageStaticPaths = async ({ locales = [] }) => {
  if (process.env.NODE_ENV === 'development') return { paths: [], fallback: 'blocking' }

  const responses = locales.map(async (locale) => {
    const staticClient = graphqlSsrClient(locale)
    const blogPosts = staticClient.query({ query: BlogPathsDocument })
    const total = Math.ceil((await blogPosts).data.pagesConnection.aggregate.count / pageSize)
    const pages: string[] = []
    for (let i = 1; i < total - 1; i++) {
      pages.push(String(i + 1))
    }
    return pages.map((page) => ({ params: { page }, locale }))
  })
  const paths = (await Promise.all(responses)).flat(1)
  return { paths, fallback: 'blocking' }
}

export const getStaticProps: GetPageStaticProps = async ({ locale, params }) => {
  const skip = Math.abs((Number(params?.page ?? '1') - 1) * pageSize)
  const client = graphqlSharedClient(locale)
  const staticClient = graphqlSsrClient(locale)
  const conf = client.query({ query: StoreConfigDocument })

  const defaultPage = hygraphPageContent(staticClient, 'blog')
  const layout = staticClient.query({ query: LayoutDocument, fetchPolicy: 'cache-first' })

  if (!(await defaultPage).data.pages?.[0]) return { notFound: true }

  const { blogPosts, notFound, count } = await getBlogPosts({
    skip,
    pageSize,
    page: params?.page,
    locale,
  })
  if (notFound) return { notFound: true }

  return {
    props: {
      ...(await defaultPage).data,
      blogPosts,
      ...(await layout).data,
      count: count || 0,
      urlEntity: { relative_url: `blog` },
      up: { href: '/blog', title: 'Blog' },
      apolloState: await conf.then(() => client.cache.extract()),
    },
    revalidate: 60 * 20,
  }
}

type GetBlogPostsResult = {
  skip?: number
  pageSize?: number
  locale?: string
  page?: string
  count?: number
  notFound?: boolean
  blogPosts: BlogListQuery['blogPosts']
}

/**
 * This function combines the blog posts from Builder.io and GraphCMS, and implements the pagination
 * logic.
 *
 * @param param0
 * @returns
 */
export const getBlogPosts = async ({
  skip,
  pageSize,
  locale,
  page,
}): Promise<GetBlogPostsResult> => {
  const staticClient = graphqlSsrClient(locale)

  // Get Blog posts from Builder.io
  const builderPostsCountTotal = await builder
    .getAll('blog-article', {
      options: { noTargeting: true },
      fields: 'data.url',
    })
    .then((posts) => posts.length)

  const builderPosts = await builder.getAll('blog-article', {
    // We only need the URL field
    options: { noTargeting: true, offset: skip },
    limit: pageSize,
  })

  // If we have enough posts from Builder.io, we don't need to query GraphCMS
  const builderPostCount = builderPosts.length
  const hygraphPostsNeeded = pageSize - builderPostCount

  const blogPosts = staticClient.query({
    query: BlogListDocument,
    variables: {
      currentUrl: ['blog'],
      first: hygraphPostsNeeded > 0 ? hygraphPostsNeeded : pageSize,
      skip:
        // If builderPostCount > 0, that means we already have posts from Builder.io, so the skip for Hygraph should be 0
        hygraphPostsNeeded > 0 && builderPostCount > 0 ? 0 : skip - builderPostsCountTotal,
    },
  })
  const blogPaths = staticClient.query({ query: BlogPathsDocument })
  const hygraphPostsCount = (await blogPaths).data.pagesConnection.aggregate.count

  if (hygraphPostsNeeded && !(await blogPosts).data?.blogPosts.length)
    return { notFound: true, blogPosts: [] }
  if (Number(page) <= 0) return { notFound: true, blogPosts: [] }

  const blogPostsAll = [
    ...builderPosts.map((post) => ({
      title: post?.data?.title,
      url: post?.data?.url.startsWith('/') ? post?.data?.url.substr(1) : post?.data?.url,
      date: post?.data?.date
        ? new Date(post?.data?.date as number).toISOString()
        : new Date().toISOString(),
      asset:
        post?.data?.heroImage || post?.data?.thumbnail
          ? {
              url: post?.data?.heroImage || post?.data?.thumbnail || '',
              width: 900,
              height: 600,
            }
          : null,
    })),
    ...(hygraphPostsNeeded > 0 ? (await blogPosts).data.blogPosts : []),
  ]

  return {
    blogPosts: blogPostsAll,
    count: builderPostsCountTotal + hygraphPostsCount,
  }
}
